import clsx from 'clsx'
import { useRouter } from 'next/router'
import React, { FormEventHandler, useCallback, useContext, useState } from 'react'

import { Button } from '@Components/Button'
import { PhonePrefixItem } from '@Domains/Checkout/Steps/Address/Components/PhonePrefixItem/PhonePrefixItem'
import {
  getPhonePrefixFromLocale,
  PhonePrefixLocaleCodeMap,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutAddressStep/Utils/PhonePrefixLocaleCodeMap'
import { ClubTerms } from '@Domains/Club/Components/ClubTerms'
import { useClub } from '@Domains/Club/Hook'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { EmailValidationV2 } from '@Shared/Components/Forms/Validations/EmailValidation'
import { NotEmptyValidationV2 } from '@Shared/Components/Forms/Validations/NotEmptyValidation'
import { PasswordValidation } from '@Shared/Components/Forms/Validations/PasswordValidation'
import { PhoneLengthValidationV2 } from '@Shared/Components/Forms/Validations/PhoneLengthValidation'
import { SelectInputCustom, useSelectInputCustomActionRef } from '@Shared/Components/Inputs/Select/SelectInputCustom'
import { TextInput, useTextInputActionRef } from '@Shared/Components/Inputs/TextInput/TextInput'

import styles from './styles.module.scss'

export const ClubCreateAccount = () => {
  const { defaultEmail, register, setModalState } = useClub()
  const { t } = useTranslation()
  const { store } = useContext(MetadataContext)
  const { locale = 'en-GB' } = useRouter()
  const [showTermsError, setShowTermsError] = useState(false)
  const [genericError, setGenericError] = useState<string | undefined>()
  const firstNameActionRef = useTextInputActionRef()
  const [firstName, setFirstName] = useState('')
  const emailActionRef = useTextInputActionRef()
  const [email, setEmail] = useState(defaultEmail || '')
  const passwordActionRef = useTextInputActionRef()
  const [password, setPassword] = useState('')
  const phonePrefixActionRef = useSelectInputCustomActionRef()
  const [phonePrefix, setPhonePrefix] = useState(getPhonePrefixFromLocale(locale))
  const phoneNumberActionRef = useTextInputActionRef()
  const [phoneNumber, setPhoneNumber] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false)
  const phonePrefixItem = useCallback(option => <PhonePrefixItem prefix={PhonePrefixLocaleCodeMap[option] || undefined} title={option} />, [])

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(async (e) => {
    e.preventDefault()
    e.stopPropagation()

    firstNameActionRef.current?.touch()
    const isFirstNameValid = firstNameActionRef.current?.validate()

    emailActionRef.current?.touch()
    const isEmailValid = emailActionRef.current?.validate()

    passwordActionRef.current?.touch()
    const isPasswordValid = passwordActionRef.current?.validate()

    phonePrefixActionRef.current?.touch()
    const isPhonePrefixValid = phonePrefixActionRef.current?.validate()

    phoneNumberActionRef.current?.touch()
    const isPhoneNumberValid = phoneNumberActionRef.current?.validate()

    if (!acceptedTerms) {
      setShowTermsError(true)
    }

    if (!isPhoneNumberValid || !isFirstNameValid || !isEmailValid || !isPasswordValid || !isPhonePrefixValid || !isPhoneNumberValid || !acceptedTerms) {
      return
    }

    const result = await register(email, password, firstName, phonePrefix, phoneNumber)
    if (result?.error) {
      setGenericError(result.error)
      return
    }

    if (result?.nextModalState) {
      setModalState(result.nextModalState)
    }
  }, [
    acceptedTerms,
    email,
    emailActionRef,
    firstName,
    firstNameActionRef,
    password,
    passwordActionRef,
    phoneNumber,
    phoneNumberActionRef,
    phonePrefix,
    phonePrefixActionRef,
    register,
    setModalState,
  ])

  return (
    <form onSubmit={onSubmit}>
      <p className={clsx(styles.bigLabel)}>{t('common:TRENDHIMCLUB_COMPLETE_SIGNUP')}</p>
      <p className={clsx(styles.formElementGab)}>{t('common:TRENDHIMCLUB_COMPLETE_SIGNUP_SUBTEXT')}</p>

      <TextInput
        actionRef={firstNameActionRef}
        autoFocus
        className={styles.formElementGab}
        id="ClubModal_Signup_First_Name"
        label={t('common:FIRST_NAME')}
        onChange={value => setFirstName(value)}
        validations={[NotEmptyValidationV2]}
        value={firstName}
      />
      <TextInput
        actionRef={emailActionRef}
        className={styles.formElementGab}
        disabled={!!defaultEmail}
        id="ClubModal_Signup_Email"
        label={t('common:EMAIL_ADDRESS')}
        onChange={value => setEmail(value)}
        validations={[EmailValidationV2]}
        value={email}
      />
      <TextInput
        actionRef={passwordActionRef}
        className={styles.formElementGab}
        id="ClubModal_Signup_Password"
        label={t('common:PASSWORD')}
        onChange={value => setPassword(value)}
        type="password"
        validations={[NotEmptyValidationV2, PasswordValidation]}
        value={password}
      />

      <div className={styles.oneLine}>
        <SelectInputCustom
          actionRef={phonePrefixActionRef}
          className={clsx(styles.phonePrefix, styles.formElementGab)}
          customElement={phonePrefixItem}
          onChange={(prefix) => {
            setPhonePrefix(prefix)
          }}
          options={Object.keys(PhonePrefixLocaleCodeMap)}
          showValid={false}
          value={phonePrefix}
        />
        <div>
          <TextInput
            actionRef={phoneNumberActionRef}
            className={styles.formElementGab}
            id="ClubModal_Signup_Phone_Number"
            label={t('common:COF_TELEPHONE_NUMBER')}
            onChange={value => setPhoneNumber(value)}
            validations={[
              NotEmptyValidationV2,
              PhoneLengthValidationV2(t, 6, store.id === '17' ? 9 : undefined),
            ]}
            value={phoneNumber}
          />
        </div>
      </div>
      <ClubTerms
        accepted={acceptedTerms}
        className={styles.formElementGab}
        onAccept={(accepted) => {
          setShowTermsError(false)
          setAcceptedTerms(accepted)
        }}
      >
        {showTermsError && (<p className={styles.error}>{t('common:COD_YOU_NEED_TO_ACCEPT_TERMS')}</p>)}
      </ClubTerms>
      <Button
        className={clsx(styles.nextButton)}
        color="black"
        style={{ margin: '0' }}
        type="submit"
      >
        {t('common:TRENDHIMCLUB_SIGN_UP_LABEL')}
      </Button>
      {genericError && <p className={styles.error}>{t(genericError)}</p>}
      <button className={clsx(styles.labelButton, styles.modeSwitch)} onClick={() => setModalState('SIGN_IN')} type="button">
        {t('common:LOGIN_LABEL')}
      </button>
    </form>
  )
}

